/**
 * Date utility functions for the scheduler
 */

/**
 * Parse an API datetime string as UTC.
 *
 * The API returns datetime strings without timezone indicator (e.g., "2026-02-02T21:40:27").
 * JavaScript's Date() interprets such strings as LOCAL time, but the API stores them as UTC.
 * This function ensures proper UTC interpretation by appending 'Z' if missing.
 *
 * @param {string} isoString - ISO datetime string from API (may or may not have Z suffix)
 * @returns {Date} Date object correctly interpreted as UTC
 */
export const parseApiDateTime = (isoString) => {
  if (!isoString) return new Date();

  // If the string doesn't end with Z or timezone offset, treat it as UTC
  const hasTimezone = /Z$|[+-]\d{2}:\d{2}$/.test(isoString);
  const utcString = hasTimezone ? isoString : `${isoString}Z`;

  return new Date(utcString);
};

/**
 * Format an API datetime string to locale string for display.
 * Properly handles UTC conversion before displaying in user's local timezone.
 *
 * @param {string} isoString - ISO datetime string from API
 * @param {object} options - Intl.DateTimeFormat options (optional)
 * @returns {string} Formatted date string in local timezone
 */
export const formatApiDateTime = (isoString, options = {}) => {
  if (!isoString) return '';

  const date = parseApiDateTime(isoString);
  return date.toLocaleString([], options);
};

/**
 * Format an API datetime string to locale time string only.
 *
 * @param {string} isoString - ISO datetime string from API
 * @param {object} options - Intl.DateTimeFormat options (optional)
 * @returns {string} Formatted time string in local timezone
 */
export const formatApiTime = (isoString, options = { hour: '2-digit', minute: '2-digit' }) => {
  if (!isoString) return '';

  const date = parseApiDateTime(isoString);
  return date.toLocaleTimeString([], options);
};

/**
 * Get the Monday of the week containing the given date.
 * Uses ISO week standard where Monday is the first day of the week.
 *
 * @param {Date} [date=new Date()] - The date to get the week start for
 * @returns {Date} Monday of that week at 00:00:00.000
 */
export const getWeekStart = (date = new Date()) => {
  const d = new Date(date);
  const day = d.getDay();
  // Adjust for Sunday (day === 0) to get previous Monday
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * Get the Sunday of the week (end of week).
 *
 * @param {Date} weekStart - The Monday of the week
 * @returns {Date} Sunday of that week at 23:59:59.999
 */
export const getWeekEnd = (weekStart) => {
  const d = new Date(weekStart);
  d.setDate(d.getDate() + 6);
  d.setHours(23, 59, 59, 999);
  return d;
};

/**
 * Check if two dates are in the same week (ISO week, Monday-Sunday).
 *
 * @param {Date} date1 - First date
 * @param {Date} date2 - Second date
 * @returns {boolean} True if dates are in the same week
 */
export const isSameWeek = (date1, date2) => {
  const week1Start = getWeekStart(date1);
  const week2Start = getWeekStart(date2);
  return week1Start.getTime() === week2Start.getTime();
};

/**
 * Format a date range for display (e.g., "Jan 20 - Jan 26, 2025").
 *
 * @param {Date} startDate - Start of the range
 * @param {Date} endDate - End of the range
 * @returns {string} Formatted date range string
 */
export const formatDateRange = (startDate, endDate) => {
  const options = { month: 'short', day: 'numeric' };
  const yearOptions = { ...options, year: 'numeric' };

  const startStr = startDate.toLocaleDateString('en-US', options);
  const endStr = endDate.toLocaleDateString('en-US', yearOptions);

  return `${startStr} - ${endStr}`;
};

/**
 * Get the start of a day (00:00:00.000).
 *
 * @param {Date} date - The date
 * @returns {Date} Start of that day
 */
export const getStartOfDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * Get the end of a day (23:59:59.999).
 *
 * @param {Date} date - The date
 * @returns {Date} End of that day
 */
export const getEndOfDay = (date) => {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
};
